import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, ExternalLink } from 'lucide-react';
import {
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EcranEnErreur,
  EnteteEcran,
  LigneFichier,
  MessageErreur,
  Pastille,
  type TonPastille,
} from '../components/ui/Primitives';
import { CarteSignatures } from '../components/dossier/CarteSignatures';
import { TOUS_LES_SITES } from '../site/context';
import { Icone } from '../components/Icone';
import { LignePiece } from '../components/dossier/LignePiece';
import { VisionneusePiece } from '../components/dossier/VisionneusePiece';
import { useDeposerPiece, useSupprimerFichierPiece } from '../lib/useApprenants';
import {
  heures,
  pourcentagePresence,
  urlDocumentApprenant,
  useMesDocumentsApprenant,
  type ContratApprenant,
  type DocumentApprenant,
  type InterlocuteurApprenant,
  type PieceApprenant,
  type SeanceApprenant,
} from '../lib/useEspaceApprenant';
import { ErreurApi } from '../lib/api';

/**
 * « Mes documents » de l'espace apprenant (CDC §8.1).
 *
 * Tout vient de `/espace-apprenant/documents`, c'est-à-dire du dossier de
 * l'apprenant connecté. Rien n'est complété par une valeur d'exemple : une
 * pièce absente du dossier n'apparaît pas, et les heures affichées sont les
 * heures **suivies**, jamais le volume prévu au programme.
 *
 * Ce qui lui est remis, et quand, est décidé par l'API
 * (`domain/documents-apprenant.ts`) : l'écran n'a aucune liste de types à lui.
 * Les documents contractuels sont rangés **par contrat** — après une rupture,
 * deux « CERFA » à plat ne disent pas lequel est lequel.
 */

/** Date civile `AAAA-MM-JJ` rendue `JJ/MM/AAAA`. */
function dateCourte(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return `${jour}/${mois}/${annee}`;
}

/**
 * Les tons viennent de l'API (`LIBELLES_PIECE`, `LIBELLES_PRESENCE`) sous forme
 * de chaînes libres : cette table les ramène au vocabulaire des pastilles, et
 * un ton inconnu retombe en neutre plutôt que de produire une classe absente.
 */
const TONS_PIECE: Record<string, TonPastille> = {
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'info',
  neutral: 'neutral',
  mint: 'mint',
};

function tonPiece(ton: string): TonPastille {
  return TONS_PIECE[ton] ?? 'neutral';
}

/** Ancre de la carte des signatures, où mène « Signer » depuis une ligne. */
const ANCRE_SIGNATURES = 'documents-a-signer';

const CLASSE_LIEN =
  'inline-flex min-h-[36px] items-center gap-1 rounded-lg px-2 text-xs font-semibold text-codialis-sky hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-codialis-sky [@media(pointer:coarse)]:min-h-[40px]';

function detailDocument(document: DocumentApprenant): string {
  const morceaux = [
    document.recuLe ? `Reçu le ${dateCourte(document.recuLe)}` : 'Présenté à votre signature',
  ];
  if (document.signeLe) morceaux.push(`signé le ${dateCourte(document.signeLe)}`);
  if (document.exemplaireSigne) morceaux.push('exemplaire signé');
  return morceaux.join(' · ');
}

/**
 * Une ligne de document : ce qu'il est, quand il a été reçu, et ce qu'il y a à
 * faire. Ouvrir le fichier éteint « Nouveau » : l'API trace l'ouverture, la
 * liste est relue juste après.
 */
function LigneDocument({ document }: { document: DocumentApprenant }): JSX.Element {
  const client = useQueryClient();
  const relire = (): void => {
    window.setTimeout(() => {
      void client.invalidateQueries({ queryKey: ['espace-apprenant'] });
    }, 1500);
  };

  const pastille =
    document.nouveau && document.etat === 'DISPONIBLE'
      ? { texte: 'Nouveau', ton: 'mint' as const }
      : { texte: document.etatTexte, ton: tonPiece(document.etatTon) };

  return (
    <LigneFichier
      nom={document.intitule}
      detail={detailDocument(document)}
      pastille={pastille}
      actions={
        !document.telechargeable ? (
          <span className="text-xs text-codialis-gray">Fichier indisponible</span>
        ) : document.etat === 'A_SIGNER' ? (
          <span className="flex flex-wrap items-center gap-1">
            <a
              href={urlDocumentApprenant(document.id, true)}
              target="_blank"
              rel="noreferrer"
              onClick={relire}
              className={CLASSE_LIEN}
            >
              Lire
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
              <span className="sr-only">(nouvel onglet)</span>
            </a>
            <a href={`#${ANCRE_SIGNATURES}`} className={CLASSE_LIEN}>
              Signer
            </a>
          </span>
        ) : (
          <a href={urlDocumentApprenant(document.id)} onClick={relire} className={CLASSE_LIEN}>
            Télécharger
            <span className="sr-only"> {document.intitule}</span>
          </a>
        )
      }
    />
  );
}

/** Un contrat, sa situation, et les documents qui s'y rapportent. */
function BlocContrat({ contrat }: { contrat: ContratApprenant }): JSX.Element {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-codialis-sand px-4 py-2.5 sm:px-6">
        <Building2 className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-codialis-navy">{contrat.employeur}</div>
          <div className="text-[11px] text-codialis-gray">
            Contrat du {dateCourte(contrat.dateDebut)} au {dateCourte(contrat.dateFin)}
            {contrat.rompuLe ? ` · rompu le ${dateCourte(contrat.rompuLe)}` : ''}
          </div>
        </div>
        <Pastille texte={contrat.etatTexte} ton={tonPiece(contrat.etatTon)} />
      </div>
      {contrat.documents.length === 0 ? (
        <p className="px-4 py-3 text-sm text-codialis-gray sm:px-6">
          Aucun document de ce contrat n’est encore à votre disposition.
        </p>
      ) : (
        <div className="divide-y divide-gray-100">
          {contrat.documents.map((document) => (
            <LigneDocument key={document.id} document={document} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Détail d'une demi-journée passée : ce qui est signé, et ce qui ne l'est pas. */
function detailDemiJournee(seance: SeanceApprenant): string {
  const base = `${seance.demiJournee} · ${seance.heureDebut}–${seance.heureFin}`;
  if (seance.signeLe) return `${base} · émargé`;
  return `${base} · ${seance.statutTexte.toLowerCase()} · non émargé`;
}

function LigneInterlocuteur({ personne }: { personne: InterlocuteurApprenant }): JSX.Element {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-[10px] font-bold text-white">
        {personne.initiales}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-codialis-navy">{personne.nom}</div>
        <div className="text-[11px] text-codialis-gray">{personne.role}</div>
        {personne.email ? (
          <a
            href={`mailto:${personne.email}`}
            className="text-[11px] text-codialis-sky hover:underline"
          >
            {personne.email}
          </a>
        ) : null}
      </div>
      {/* Le tuteur en entreprise n'a pas de compte : pas de fil possible, donc
          pas de bouton — plutôt qu'un bouton désactivé sans explication. */}
      {personne.utilisateurId ? (
        <Link
          to={`/messages?contact=${personne.utilisateurId}`}
          title={`Écrire à ${personne.nom}`}
          aria-label={`Écrire à ${personne.nom}`}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-codialis-sky transition hover:bg-white hover:text-codialis-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-codialis-sky"
        >
          <Icone nom="message" className="h-4 w-4" />
        </Link>
      ) : null}
    </div>
  );
}

export function AppDocumentsPage(): JSX.Element {
  const naviguer = useNavigate();
  const { data, isLoading, error } = useMesDocumentsApprenant();
  const deposer = useDeposerPiece();
  const supprimerFichier = useSupprimerFichierPiece();
  // Consultation d'un fichier : la pièce et le rang du fichier cliqué, pour
  // pouvoir passer du recto au verso sans refermer la fenêtre.
  const [consultation, setConsultation] = useState<{ piece: PieceApprenant; index: number } | null>(
    null,
  );

  if (isLoading) return <Chargement libelle="Chargement de vos documents…" />;
  if (error) {
    return (
      <EcranEnErreur
        titre="Mes documents"
        message={error instanceof ErreurApi
            ? error.message
            : 'Vos documents n’ont pas pu être chargés.'}
      />
    );
  }
  if (!data) return <EcranEnErreur titre="Mes documents" message="Vos documents n’ont pas pu être chargés." />;

  const { emargement } = data;
  const progression =
    emargement.heuresPrevues > 0
      ? Math.min(100, Math.round((emargement.heuresSuivies / emargement.heuresPrevues) * 100))
      : 0;
  const sansDocumentContractuel =
    data.contrats.length === 0 && data.documentsContractuels.length === 0;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Mes documents"
        sousTitre="Espace apprenant · documents reçus, conventions, contrats et émargement"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Documents reçus"
          valeur={data.documentsRecus}
          detail={
            data.nouveaux > 0
              ? `dont ${data.nouveaux} pas encore ouvert${data.nouveaux > 1 ? 's' : ''}`
              : 'du pédagogique et de l’administratif'
          }
          ton="navy"
        />
        <Compteur
          libelle="À signer"
          valeur={data.signaturesAttendues.length}
          detail="signatures en attente de votre part"
          ton="warm"
        />
        <Compteur
          libelle="Pièces à déposer"
          valeur={data.piecesAFournir}
          detail="pièces obligatoires manquantes"
          ton="red"
        />
        <Compteur
          libelle="Présence"
          valeur={pourcentagePresence(emargement.tauxPresence)}
          detail={
            emargement.tauxPresence === null
              ? 'aucune demi-journée pointée'
              : `${heures(emargement.heuresSuivies)} suivies`
          }
          ton="mint"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Carte titre="Mon contrat et mes conventions" sansBordure>
            {sansDocumentContractuel ? (
              <p className="px-4 py-4 text-sm text-codialis-gray sm:px-6">
                Aucun contrat ni aucune convention n’est encore à votre disposition.
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {data.contrats.map((contrat) => (
                  <BlocContrat key={contrat.id} contrat={contrat} />
                ))}
                {data.documentsContractuels.length > 0 && (
                  <div>
                    {data.contrats.length > 0 && (
                      <div className="bg-codialis-sand px-4 py-2.5 text-sm font-semibold text-codialis-navy sm:px-6">
                        Autres documents contractuels
                      </div>
                    )}
                    <div className="divide-y divide-gray-100">
                      {data.documentsContractuels.map((document) => (
                        <LigneDocument key={document.id} document={document} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Carte>

          <Carte titre="Documents de formation">
            {data.documentsFormation.length === 0 ? (
              <p className="px-4 py-4 text-sm text-codialis-gray sm:px-6">
                Aucun document de formation n’est encore à votre disposition.
              </p>
            ) : (
              data.documentsFormation.map((document) => (
                <LigneDocument key={document.id} document={document} />
              ))
            )}
          </Carte>

          {/* La signature se fait ici, sur place. L'API ne lui rend que ses
              propres demandes, et ne le laisse signer que sa ligne : celles de
              l'employeur ou du représentant légal se recueillent sur la
              tablette de l'équipe. */}
          <div id={ANCRE_SIGNATURES} className="scroll-mt-4">
            <CarteSignatures
              siteCode={TOUS_LES_SITES}
              peutDemander={false}
              titre="Documents à signer"
              lienDocument={(documentId) => urlDocumentApprenant(documentId, true)}
              signataireAutorise={(role) => role === 'APPRENANT'}
            />
          </div>

          <Carte titre="Pièces que je dois déposer" sansBordure>
            {(deposer.error || supprimerFichier.error) && (
              <div className="px-4 sm:px-6 pt-3">
                <MessageErreur
                  message={(() => {
                    const erreur = deposer.error ?? supprimerFichier.error;
                    return erreur instanceof ErreurApi
                      ? erreur.message
                      : 'L’opération n’a pas abouti.';
                  })()}
                />
              </div>
            )}
            <div className="divide-y divide-gray-100">
              {data.pieces.length === 0 ? (
                <p className="px-4 sm:px-6 py-4 text-sm text-codialis-gray">
                  Aucune pièce n’est attendue pour votre dossier.
                </p>
              ) : (
                data.pieces.map((piece) => (
                  <LignePiece
                    key={piece.id}
                    piece={piece}
                    gestionAdministrative={false}
                    consultationSeule={false}
                    enCours={deposer.isPending || supprimerFichier.isPending}
                    onValider={() => {}}
                    onVerifier={() => {}}
                    onRefuser={() => {}}
                    onDeposer={(fichier, remplace) =>
                      deposer.mutate({ pieceId: piece.id, fichier, ...(remplace ? { remplace } : {}) })
                    }
                    onSupprimerFichier={(fichierId) =>
                      supprimerFichier.mutate({ pieceId: piece.id, fichierId })
                    }
                    onConsulter={(index) => setConsultation({ piece, index })}
                  />
                ))
              )}
            </div>
            {data.piecesAFournir > 0 ? (
              <div className="bg-amber-50 px-4 sm:px-6 py-3 text-xs text-amber-900">
                Il vous reste {data.piecesAFournir}{' '}
                {data.piecesAFournir > 1 ? 'pièces obligatoires' : 'pièce obligatoire'} à
                déposer. Une relance automatique est envoyée tant que le dossier est incomplet.
              </div>
            ) : null}
          </Carte>
        </div>

        <div className="space-y-6">
          <Carte titre="Mon émargement" sansBordure>
            <div className="space-y-3 p-4 sm:p-6">
              <div>
                <div className="mb-1.5 flex justify-between gap-2 text-xs text-codialis-gray">
                  <span>Heures suivies</span>
                  <span className="font-bold text-codialis-navy">
                    {heures(emargement.heuresSuivies)} / {heures(emargement.heuresPrevues)}
                  </span>
                </div>
                <div className="progress-track h-2 overflow-hidden rounded-full">
                  <div className="progress-bar h-full" style={{ width: `${progression}%` }} />
                </div>
                <p className="mt-1.5 text-[11px] text-codialis-gray">
                  Temps réellement passé en formation sur les demi-journées déjà pointées : ce
                  que votre certificat atteste. Le volume prévu au programme n’entre pas dans ce
                  calcul.
                </p>
              </div>

              {data.dernieresDemiJournees.length === 0 ? (
                <p className="text-sm text-codialis-gray">Aucune demi-journée passée.</p>
              ) : (
                data.dernieresDemiJournees.map((seance) => (
                  <div
                    key={seance.presenceId}
                    className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-codialis-navy">
                        {dateCourte(seance.date)} · {seance.libelle}
                      </div>
                      <div className="text-[11px] text-codialis-gray">
                        {detailDemiJournee(seance)}
                      </div>
                    </div>
                    <Pastille texte={seance.statutTexte} ton={tonPiece(seance.statutTon)} />
                  </div>
                ))
              )}

              <BoutonSecondaire onClick={() => naviguer('/emargement')}>
                Tout mon émargement
              </BoutonSecondaire>
            </div>
          </Carte>

          <Carte titre="Mes interlocuteurs" sansBordure>
            <div className="space-y-2 p-4 sm:p-6">
              {data.interlocuteurs.length === 0 ? (
                <p className="text-sm text-codialis-gray">
                  Aucun interlocuteur n’est encore rattaché à votre dossier.
                </p>
              ) : (
                data.interlocuteurs.map((personne) => (
                  <LigneInterlocuteur key={`${personne.role}-${personne.nom}`} personne={personne} />
                ))
              )}
            </div>
          </Carte>
        </div>
      </div>

      {/* La visionneuse n'est montée qu'à l'ouverture : elle garde le contenu
          d'une pièce justificative en mémoire, il n'a pas à y rester après
          fermeture. */}
      {consultation && (
        <VisionneusePiece
          ouverte
          libellePiece={consultation.piece.libelle}
          fichiers={consultation.piece.fichiers}
          indexInitial={consultation.index}
          pieceId={consultation.piece.id}
          onFermer={() => setConsultation(null)}
        />
      )}
    </div>
  );
}
